import { Component, effect, inject, input, OnDestroy, OnInit, output, viewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  CallFlowGreetingsEntity,
  CallFlowGreetingsFormComponent,
} from '../call-flow-greetings/call-flow-greetings-form.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TeamsAutoAttendantsMenuButtonsComponent } from '../auto-attendants-menu-buttons/teams-auto-attendants-menu-buttons.component';
import { UiCallFlow } from '../call-flows-side-nav/call-flows-side-nav.component';
import { TeamsAutoAttendantsDestinationsSearchService } from '../../../teams-auto-attendants-destinations-search.service';
import { TeamsAutoAttendantEditContext } from '../../../teams-auto-attendant-edit.context';
import { TeamsAutoAttendantsService } from '../../../teams-auto-attendants.service';
import { transformEnumToHumanReadableLabel } from '../../../auto-attendants-util';
import { isEqual } from 'lodash';
import { E164_REGEX } from '../menu-button-form/menu-button-form.component';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../../forms/fields/select/ziro-select.component';
import {
  CallRoutingOption,
  DirectorySearchMethod,
  RedirectCallSettingsType,
  RedirectCallSettingsView,
} from '../../../../shared/models/generated/ziroModels';
import { MicrosoftTeamsUserSearchResource } from '../../../../helpdesk/shared/resources/microsoft-teams-user-search.resource';
import { Microsoft365GroupResource } from '../../../../helpdesk/shared/resources/microsoft-365-group.resource';
import { MicrosoftCallQueueSearchResource } from '../../../../helpdesk/shared/resources/microsoft-call-queue-search.resource';
import { MicrosoftAutoAttendantsSearchResource } from '../../../../helpdesk/shared/resources/microsoft-auto-attendants-search.resource';
import { ZiroFormsModule } from '../../../../forms/ziro-forms.module';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { ZiroRadioConfig } from '../../../../forms/fields/radio/ziro-radio.component';
import { HtmlInputType, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import { Observable, of, Subscription } from 'rxjs';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';

interface DestinationsEntity {
  callRoutingOption: CallRoutingOption;
  directorySearchMethod: ZiroSelectOption | string;
  redirectType: ZiroSelectOption;
  redirectDestination: ZiroSelectOption;
  redirectDestinationNumber: string;
}

@Component({
  selector: 'ziro-call-flow-destinations-form',
  standalone: true,
  templateUrl: './call-flow-destinations-form.component.html',
  styleUrl: './call-flow-destinations-form.component.scss',
  providers: [
    TeamsAutoAttendantsDestinationsSearchService,
    MicrosoftTeamsUserSearchResource,
    Microsoft365GroupResource,
    MicrosoftCallQueueSearchResource,
    MicrosoftAutoAttendantsSearchResource,
  ],
  imports: [
    ZiroFormsModule,
    TranslateModule,
    CallFlowGreetingsFormComponent,
    ReactiveFormsModule,
    FormsModule,
    TeamsAutoAttendantsMenuButtonsComponent,
  ],
})
export class CallFlowDestinationsFormComponent
  extends ZiroFormAbstractDirective<DestinationsEntity>
  implements OnInit, OnDestroy
{
  menuButtons = viewChild(TeamsAutoAttendantsMenuButtonsComponent);
  greetingsForm = viewChild(CallFlowGreetingsFormComponent);
  tenantId = input.required<string>();
  callFlow = input.required<UiCallFlow>();

  greetingUpdate = output<CallFlowGreetingsEntity>();

  private _editContext = inject(TeamsAutoAttendantEditContext);
  private _searchService = inject(TeamsAutoAttendantsDestinationsSearchService);
  private _teamsAutoAttendantsService = inject(TeamsAutoAttendantsService);

  formConfig: ZiroFormConfig = {
    fields: {
      callRoutingOption: {
        label: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.action.label',
        dataAutomation: 'call-flow-destination-action',
        componentConfig: new ZiroRadioConfig<CallRoutingOption>({
          buttons: [
            {
              value: CallRoutingOption.PLAY_MENU_OPTIONS,
              label: 'Play Menu Options',
            },
            {
              value: CallRoutingOption.REDIRECT_CALL,
              label: 'Redirect Call',
            },
            {
              value: CallRoutingOption.DISCONNECT,
              label: 'Disconnect',
            },
          ],
          inline: true,
        }),
      },
      redirectType: {
        label: () => 'tkey;admin.teams_voice_management.teams_auto_attendant.call_flow.redirect_to.label',
        dataAutomation: 'redirect-to-select',
        componentConfig: new ZiroSelectConfig({
          options: [
            {
              value: RedirectCallSettingsType.USER,
              label: transformEnumToHumanReadableLabel(RedirectCallSettingsType.USER),
            },
            {
              value: RedirectCallSettingsType.VOICE_APP,
              label: transformEnumToHumanReadableLabel(RedirectCallSettingsType.VOICE_APP),
            },
            {
              value: RedirectCallSettingsType.RESOURCE_ACCOUNT,
              label: transformEnumToHumanReadableLabel(RedirectCallSettingsType.RESOURCE_ACCOUNT),
            },
            {
              value: RedirectCallSettingsType.VOICEMAIL,
              label: transformEnumToHumanReadableLabel(RedirectCallSettingsType.VOICEMAIL),
            },
            {
              value: RedirectCallSettingsType.EXTERNAL_PHONE_NUMBER,
              label: transformEnumToHumanReadableLabel(RedirectCallSettingsType.EXTERNAL_PHONE_NUMBER),
            },
          ],
          hideClear: true,
        }),
        required: () => this._isRedirectCall,
        hidden: () => !this._isRedirectCall,
        valExcluded: () => !this._isRedirectCall,
      },
      redirectDestination: {
        label: () => 'tkey;admin.teams_voice_management.teams_auto_attendant.call_flow.redirect_to.destination',
        dataAutomation: 'redirect-destination-select',
        componentConfig: this._buildRedirectDestinationConfig(),
        hidden: () => !this._isRedirectCall || !this.formData?.redirectType || this._isExternalPhoneRedirect,
        valExcluded: () => !this._isRedirectCall || !this.formData?.redirectType || this._isExternalPhoneRedirect,
        required: () => this._isRedirectCall && !!this.formData?.redirectType && !this._isExternalPhoneRedirect,
      },
      redirectDestinationNumber: {
        label: () => 'tkey;admin.teams_voice_management.teams_auto_attendant.call_flow.redirect_to.destination',
        dataAutomation: 'redirect-destination-number',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
        hidden: () => !this._isRedirectCall || !this._isExternalPhoneRedirect,
        valExcluded: () => !this._isRedirectCall || !this._isExternalPhoneRedirect,
        required: () => this._isRedirectCall && this._isExternalPhoneRedirect,
        validation: [
          {
            validator: (val: string) =>
              !val || E164_REGEX.test(val) ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
            message: 'tkey;validators.global.error.invalid_number.pattern',
          },
        ],
      },
      directorySearchMethod: {
        label: () =>
          'tkey;admin.teams_voice_management.teams_auto_attendant.destinations.call_flow_actions.directory_search.label',
        dataAutomation: 'directory-search-select',
        componentConfig: new ZiroSelectConfig({
          options: [
            {
              value: DirectorySearchMethod.NONE,
              label: transformEnumToHumanReadableLabel(DirectorySearchMethod.NONE),
            },
            {
              value: DirectorySearchMethod.DIAL_BY_NAME,
              label: transformEnumToHumanReadableLabel(DirectorySearchMethod.DIAL_BY_NAME),
            },
            {
              value: DirectorySearchMethod.DIAL_BY_EXTENSION,
              label: transformEnumToHumanReadableLabel(DirectorySearchMethod.DIAL_BY_EXTENSION),
            },
          ],
          hideClear: true,
        }),
        required: () => this._isPlayMenuOptions,
        hidden: () => !this._isPlayMenuOptions,
      },
    },
  };

  ziroIcons = ZiroIcons;
  private _suppressNextUpdate = false;
  private _subscriptions = new Subscription();

  constructor() {
    super();
    effect(() => {
      const flowId = this.callFlow()?.id;
      const allFlows = [...this._editContext.standardCallFlows(), ...this._editContext.localHolidayCallFlows()];
      const currentFlow = allFlows.find((f) => f.id === flowId);
      const hasButtons =
        !!currentFlow?.playMenuOptionSettings?.dialKeys &&
        Object.values(currentFlow.playMenuOptionSettings.dialKeys).some(Boolean);
      this._updateDirectorySearchMethodConfig(hasButtons);
    });
  }

  ngOnInit(): void {
    const updateSub = this.ziroFormsUpdate$.subscribe((update) => this._handleFormUpdate(update));
    this._subscriptions.add(updateSub);

    this.entitySource.next(this._mapFlowToEntity(this.callFlow()));
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscriptions.unsubscribe();
  }

  validateAndShowErrors(): boolean {
    this._setIsFormSubmitted(true);
    this.validateAllFields();

    const greetingsValid = this._validateChildForm(this.greetingsForm());
    const menuButtonsValid = this.menuButtons()?.validateAndShowErrors() ?? true;
    return this.isFormValid() && greetingsValid && menuButtonsValid;
  }

  private get _isRedirectCall(): boolean {
    return this.formData?.callRoutingOption === CallRoutingOption.REDIRECT_CALL;
  }

  private get _isPlayMenuOptions(): boolean {
    return this.formData?.callRoutingOption === CallRoutingOption.PLAY_MENU_OPTIONS;
  }

  private get _isExternalPhoneRedirect(): boolean {
    return this.formData?.redirectType?.value === RedirectCallSettingsType.EXTERNAL_PHONE_NUMBER;
  }

  private _fetchRedirectDestination(query: string): Observable<ZiroSelectOption[]> {
    const type = this.formData?.redirectType.value;
    if (!query || query.length < 3 || !type) {
      return of([]);
    }

    switch (type) {
      case RedirectCallSettingsType.USER:
        return this._searchService.searchUsers(this.tenantId(), query);
      case RedirectCallSettingsType.RESOURCE_ACCOUNT:
        return this._searchService.searchResourceAccounts(this.tenantId(), query);
      case RedirectCallSettingsType.VOICEMAIL:
        return this._searchService.searchVoicemailGroups(this.tenantId(), query);
      case RedirectCallSettingsType.VOICE_APP:
        return this._searchService.searchVoiceApps(this.tenantId(), query);
      default:
        return of([]);
    }
  }

  private _handleFormUpdate(update: { new: DestinationsEntity; old: DestinationsEntity }): void {
    if (this._shouldSkipUpdate()) {
      return;
    }

    if (!update.new || !update.old || isEqual(update.new, update.old)) {
      return;
    }

    const { new: next, old: prev } = update;
    const routingChanged = next.callRoutingOption !== prev.callRoutingOption;
    const typeChanged = !isEqual(next.redirectType?.value, prev.redirectType?.value);
    const destinationChanged = !isEqual(next.redirectDestination, prev.redirectDestination);
    const destinationNumberChanged = next.redirectDestinationNumber !== prev.redirectDestinationNumber;
    const directoryChanged = next.directorySearchMethod !== prev.directorySearchMethod;

    if (!routingChanged && !typeChanged && !destinationChanged && !destinationNumberChanged && !directoryChanged) {
      return;
    }
    if (routingChanged && this._restoreExistingSettingsForRoutingOption(next)) {
      return;
    }
    if (typeChanged) {
      this._resetRedirectDestination(next);
      return;
    }

    this._updateCallRoutingSettings(next);
  }

  private _shouldSkipUpdate(): boolean {
    if (!this._suppressNextUpdate) {
      return false;
    }
    this._suppressNextUpdate = false;
    return true;
  }

  private _restoreExistingSettingsForRoutingOption(next: DestinationsEntity): boolean {
    const flow = this.callFlow();
    const entity = (flow as any).redirectCallSettingsEntity;

    if (this._isRedirectCall && flow.redirectCallSettings) {
      const destination = flow.redirectCallSettings.destination;
      const isExternalPhone = flow.redirectCallSettings.type === RedirectCallSettingsType.EXTERNAL_PHONE_NUMBER;
      const formattedDestination = isExternalPhone
        ? this._teamsAutoAttendantsService.formatNumberFromApi(destination)
        : destination;

      this._suppressAndEmit({
        ...next,
        redirectType: this._toSelectOption(flow.redirectCallSettings.type),
        redirectDestination:
          !isExternalPhone && formattedDestination
            ? { label: entity?.displayName ?? formattedDestination, value: formattedDestination }
            : null,
        redirectDestinationNumber: isExternalPhone ? (formattedDestination ?? '') : '',
      });
      return true;
    }

    if (
      next.callRoutingOption === CallRoutingOption.PLAY_MENU_OPTIONS &&
      flow.playMenuOptionSettings?.directorySearchMethod
    ) {
      this._suppressAndEmit({
        ...next,
        directorySearchMethod: this._toSelectOption(flow.playMenuOptionSettings.directorySearchMethod),
      });
      return true;
    }

    this._suppressNextUpdate = false;
    return false;
  }

  private _resetRedirectDestination(update: DestinationsEntity): void {
    this._suppressAndEmit({
      ...update,
      redirectDestination: null,
      redirectDestinationNumber: '',
    });
    this.fieldChannels?.['redirectDestination']?.componentConfigSource.next(this._buildRedirectDestinationConfig());
  }

  private _updateCallRoutingSettings(next: DestinationsEntity): void {
    const redirectCallSettings = this._toRedirectCallSettings(next);
    this._editContext.updateCallRoutingSettings(
      this.callFlow().id,
      next.callRoutingOption,
      redirectCallSettings,
      typeof next.directorySearchMethod === 'object' ? next.directorySearchMethod.value : next.directorySearchMethod,
    );
  }

  private _toRedirectCallSettings(next: DestinationsEntity): RedirectCallSettingsView {
    if (next.callRoutingOption !== CallRoutingOption.REDIRECT_CALL || !next.redirectType) {
      return null;
    }

    const isExternalPhone = next.redirectType?.value === RedirectCallSettingsType.EXTERNAL_PHONE_NUMBER;
    const rawDestination = isExternalPhone
      ? (next.redirectDestinationNumber ?? '')
      : next.redirectType?.value === RedirectCallSettingsType.VOICEMAIL
        ? next.redirectDestination?.value?.id
        : (next.redirectDestination?.value ?? '');

    return {
      type: next.redirectType.value,
      destination: isExternalPhone
        ? this._teamsAutoAttendantsService.formatNumberToApi(rawDestination)
        : rawDestination,
      entity: next.redirectDestination?.value?.guid ? next.redirectDestination?.value : null,
    };
  }

  private _mapFlowToEntity(flow: UiCallFlow): DestinationsEntity {
    const destination = flow.redirectCallSettings?.destination;
    const type = flow.redirectCallSettings?.type;
    const entity = (flow as any).redirectCallSettingsEntity;
    const isExternalPhone = type === RedirectCallSettingsType.EXTERNAL_PHONE_NUMBER;
    const formattedDestination = isExternalPhone
      ? this._teamsAutoAttendantsService.formatNumberFromApi(destination)
      : destination;
    const destinationOption =
      !isExternalPhone && formattedDestination
        ? { label: entity?.displayName ?? formattedDestination, value: formattedDestination }
        : null;

    return {
      callRoutingOption: flow.callRoutingOption ?? CallRoutingOption.DISCONNECT,
      redirectType: type ? this._toSelectOption(type) : null,
      redirectDestination: destinationOption,
      redirectDestinationNumber: isExternalPhone ? (formattedDestination ?? '') : '',
      directorySearchMethod: flow.playMenuOptionSettings?.directorySearchMethod
        ? this._toSelectOption(flow.playMenuOptionSettings.directorySearchMethod)
        : '',
    };
  }

  private _toSelectOption(value: string): ZiroSelectOption {
    if (!value) {
      return null;
    }
    return { label: transformEnumToHumanReadableLabel(value), value };
  }

  private _suppressAndEmit(entity: DestinationsEntity): void {
    this._suppressNextUpdate = true;
    this.entitySource.next(entity);
  }

  private _buildRedirectDestinationConfig(): ZiroSelectConfig {
    return new ZiroSelectConfig({
      isMultiSelect: false,
      keepSearchInput: true,
      minSearchLength: 3,
      asyncOptionsFn: this._fetchRedirectDestination.bind(this),
    });
  }

  private _validateChildForm(form: { triggerSubmitValidation(): void; isFormValid(): boolean } | undefined): boolean {
    if (!form) {
      return true;
    }
    form.triggerSubmitValidation();
    return form.isFormValid();
  }

  private _updateDirectorySearchMethodConfig(hasButtons: boolean): void {
    this.fieldChannels?.['directorySearchMethod']?.componentConfigSource.next(
      new ZiroSelectConfig({
        options: [
          {
            value: DirectorySearchMethod.NONE,
            label: transformEnumToHumanReadableLabel(DirectorySearchMethod.NONE),
            disabled: !hasButtons,
            disabledTooltip: !hasButtons
              ? 'tkey;admin.teams_voice_management.teams_auto_attendant.call_flow.directory_search.none_disabled_tooltip'
              : undefined,
          },
          {
            value: DirectorySearchMethod.DIAL_BY_NAME,
            label: transformEnumToHumanReadableLabel(DirectorySearchMethod.DIAL_BY_NAME),
          },
          {
            value: DirectorySearchMethod.DIAL_BY_EXTENSION,
            label: transformEnumToHumanReadableLabel(DirectorySearchMethod.DIAL_BY_EXTENSION),
          },
        ],
        hideClear: true,
      }),
    );
  }

  protected submit(): Observable<null> {
    return of(null);
  }

  protected readonly CallRoutingOption = CallRoutingOption;
}
